// app/frontend/pages/Admin/Users/Index.tsx
import { useState, useEffect } from "react";
import {
  Box,
  Stack,
  Group,
  Table,
  Badge,
  ActionIcon,
  Menu,
  TextInput,
  Select,
  Pagination,
  Modal,
  Avatar,
  Grid,
  Card,
  SegmentedControl,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import {
  IconSearch,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconShieldCheck,
  IconShieldOff,
  IconUser,
  IconLayoutGrid,
  IconLayoutList,
} from "@tabler/icons-react";
import { Link, router } from "@inertiajs/react";
import { L2KH2, L2KText, L2KCaption } from "../../../components/ui/Typography";
import { L2KButton } from "../../../components/ui/Forms";
import { l2kNotify } from "../../../components/ui/Feedback";
import AdminLayout from "../../../components/layouts/AdminLayout";
import { SEO } from "../../../components/common/SEO";

interface User {
  id: number;
  name: string;
  email: string;
  admin: boolean;
  subscription_status: string;
  created_at: string;
  updated_at: string;
}

interface Props {
  users: User[];
  filters: {
    role: string | null;
    status: string | null;
    search: string | null;
  };
  pagination: {
    page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
  stats: {
    total: number;
    admins: number;
    active: number;
    recent: number;
  };
}

export default function UsersIndex({ users, filters, pagination, stats }: Props) {
  const [search, setSearch] = useState(filters.search || "");
  const [deleteModal, { open: openDelete, close: closeDelete }] = useDisclosure(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  const isTablet = useMediaQuery("(max-width: 768px)");
  const [viewMode, setViewMode] = useState<string>("table");

  useEffect(() => {
    if (isTablet !== undefined) {
      setViewMode(isTablet ? "cards" : "table");
    }
  }, [isTablet]);

  const handleFilter = (key: string, value: string | null) => {
    const params = new URLSearchParams(window.location.search);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete("page");
    router.get(`/admin/users?${params.toString()}`);
  };

  const handleSearch = () => {
    handleFilter("search", search || null);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(window.location.search);
    params.set("page", page.toString());
    router.get(`/admin/users?${params.toString()}`);
  };

  const handleToggleAdmin = async (user: User) => {
    try {
      const response = await fetch(`/admin/users/${user.id}/toggle_admin`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
          Accept: "application/json",
        },
      });

      const data = await response.json();

      if (data.success) {
        l2kNotify.success(
          "Updated",
          `${user.name} is ${data.admin ? "now an admin" : "no longer an admin"}`
        );
        router.reload();
      } else {
        l2kNotify.error("Error", data.error || "Failed to update admin status");
      }
    } catch (error) {
      l2kNotify.error("Error", "Failed to update admin status");
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    try {
      const response = await fetch(`/admin/users/${userToDelete.id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
          Accept: "application/json",
        },
      });

      const data = await response.json();

      if (data.success) {
        l2kNotify.success("Deleted", `${userToDelete.name} has been deleted`);
        closeDelete();
        router.reload();
      } else {
        l2kNotify.error("Error", data.error || "Failed to delete user");
      }
    } catch (error) {
      l2kNotify.error("Error", "Failed to delete user");
    }
  };

  const confirmDelete = (user: User) => {
    setUserToDelete(user);
    openDelete();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active": return "green";
      case "trialing": return "blue";
      case "canceled": return "red";
      case "past_due": return "orange";
      default: return "gray";
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // User Card Component
  const UserCard = ({ user }: { user: User }) => (
    <Card
      padding="md"
      radius="md"
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
      }}
    >
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            <Avatar
              size="md"
              radius="xl"
              color="orange"
              styles={{
                placeholder: {
                  backgroundColor: "rgba(255, 152, 0, 0.2)",
                  color: "#FF9800",
                },
              }}
            >
              {getInitials(user.name || user.email)}
            </Avatar>
            <Box>
              <L2KText size="sm" fw={500}>{user.name || "No name"}</L2KText>
              <L2KCaption variant="tertiary" size="xs">{user.email}</L2KCaption>
            </Box>
          </Group>
          <Menu position="bottom-end" withArrow>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" size="sm">
                <IconDotsVertical size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown
              styles={{
                dropdown: {
                  backgroundColor: "rgba(23, 23, 23, 0.95)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <Menu.Item
                leftSection={<IconEdit size={14} />}
                component={Link}
                href={`/admin/users/${user.id}/edit`}
              >
                Edit
              </Menu.Item>
              <Menu.Item
                leftSection={user.admin ? <IconShieldOff size={14} /> : <IconShieldCheck size={14} />}
                onClick={() => handleToggleAdmin(user)}
              >
                {user.admin ? "Remove Admin" : "Make Admin"}
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={() => confirmDelete(user)}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>

        <Group justify="space-between" align="center">
          <Group gap={6}>
            {user.admin ? (
              <Badge color="orange" variant="filled" size="xs" leftSection={<IconShieldCheck size={10} />}>
                Admin
              </Badge>
            ) : (
              <Badge color="gray" variant="light" size="xs" leftSection={<IconUser size={10} />}>
                Member
              </Badge>
            )}
            <Badge
              color={getStatusColor(user.subscription_status)}
              variant="light"
              size="xs"
            >
              {user.subscription_status || "none"}
            </Badge>
          </Group>
          <L2KCaption variant="tertiary" size="xs">{user.created_at}</L2KCaption>
        </Group>
      </Stack>
    </Card>
  );

  return (
    <>
      <SEO title="Users - Admin - Learntokiz" />
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Box>
            <L2KH2 mb="xs">Users</L2KH2>
            <L2KText variant="secondary">
              {stats.total} total • {stats.admins} admins • {stats.active} active • {stats.recent} new this week
            </L2KText>
          </Box>
        </Group>

        {/* Filters + View Toggle */}
        <Group justify="space-between" wrap="wrap">
          <Group wrap="wrap">
            <TextInput
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              leftSection={<IconSearch size={16} />}
              styles={{
                input: {
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  borderColor: "rgba(255, 255, 255, 0.1)",
                  color: "#F0F0F0",
                },
              }}
            />
            <Select
              placeholder="Role"
              value={filters.role}
              onChange={(value) => handleFilter("role", value)}
              data={[
                { value: "admin", label: "Admins" },
                { value: "member", label: "Members" },
              ]}
              clearable
              styles={{
                input: {
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  borderColor: "rgba(255, 255, 255, 0.1)",
                  color: "#F0F0F0",
                },
              }}
            />
            <Select
              placeholder="Subscription"
              value={filters.status}
              onChange={(value) => handleFilter("status", value)}
              data={[
                { value: "active", label: "Active" },
                { value: "trialing", label: "Trialing" },
                { value: "canceled", label: "Canceled" },
                { value: "past_due", label: "Past Due" },
              ]}
              clearable
              styles={{
                input: {
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  borderColor: "rgba(255, 255, 255, 0.1)",
                  color: "#F0F0F0",
                },
              }}
            />
          </Group>

          {/* View Toggle */}
          <SegmentedControl
            value={viewMode}
            onChange={setViewMode}
            data={[
              {
                value: "table",
                label: (
                  <Group gap={4}>
                    <IconLayoutList size={16} />
                    <span style={{ display: isTablet ? "none" : "inline" }}>Table</span>
                  </Group>
                ),
              },
              {
                value: "cards",
                label: (
                  <Group gap={4}>
                    <IconLayoutGrid size={16} />
                    <span style={{ display: isTablet ? "none" : "inline" }}>Cards</span>
                  </Group>
                ),
              },
            ]}
            styles={{
              root: {
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 152, 0, 0.15)",
              },
              label: {
                color: "#94a3b8",
                fontWeight: 500,
              },
              indicator: {
                backgroundColor: "rgba(255, 152, 0, 0.2)",
                border: "1px solid rgba(255, 152, 0, 0.4)",
              },
            }}
          />
        </Group>

        {/* Table View */}
        {viewMode === "table" && (
          <Box style={{ overflowX: "auto" }}>
            <Table
              horizontalSpacing="md"
              verticalSpacing="sm"
              styles={{
                table: { backgroundColor: "transparent", minWidth: "700px" },
                th: {
                  color: "#888",
                  borderColor: "rgba(255, 255, 255, 0.1)",
                  fontFamily: "'Public Sans', sans-serif",
                },
                td: { borderColor: "rgba(255, 255, 255, 0.05)" },
              }}
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>User</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Subscription</Table.Th>
                  <Table.Th>Joined</Table.Th>
                  <Table.Th style={{ width: 60 }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {users.map((user) => (
                  <Table.Tr key={user.id}>
                    <Table.Td>
                      <Group gap="sm" wrap="nowrap">
                        <Avatar
                          size="sm"
                          radius="xl"
                          color="orange"
                          styles={{
                            placeholder: {
                              backgroundColor: "rgba(255, 152, 0, 0.2)",
                              color: "#FF9800",
                            },
                          }}
                        >
                          {getInitials(user.name || user.email)}
                        </Avatar>
                        <Box>
                          <L2KText size="sm" fw={500}>
                            {user.name || "No name"}
                          </L2KText>
                          <L2KCaption variant="tertiary">{user.email}</L2KCaption>
                        </Box>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {user.admin ? (
                        <Badge color="orange" variant="filled" leftSection={<IconShieldCheck size={12} />}>
                          Admin
                        </Badge>
                      ) : (
                        <Badge color="gray" variant="light" leftSection={<IconUser size={12} />}>
                          Member
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={getStatusColor(user.subscription_status)}
                        variant="light"
                      >
                        {user.subscription_status || "none"}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <L2KCaption variant="tertiary">{user.created_at}</L2KCaption>
                    </Table.Td>
                    <Table.Td>
                      <Menu position="bottom-end" withArrow>
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray">
                            <IconDotsVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown
                          styles={{
                            dropdown: {
                              backgroundColor: "rgba(23, 23, 23, 0.95)",
                              border: "1px solid rgba(255, 255, 255, 0.1)",
                            },
                          }}
                        >
                          <Menu.Item
                            leftSection={<IconEdit size={14} />}
                            component={Link}
                            href={`/admin/users/${user.id}/edit`}
                          >
                            Edit
                          </Menu.Item>
                          <Menu.Item
                            leftSection={user.admin ? <IconShieldOff size={14} /> : <IconShieldCheck size={14} />}
                            onClick={() => handleToggleAdmin(user)}
                          >
                            {user.admin ? "Remove Admin" : "Make Admin"}
                          </Menu.Item>
                          <Menu.Divider />
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            color="red"
                            onClick={() => confirmDelete(user)}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        )}

        {/* Card View */}
        {viewMode === "cards" && (
          <Grid>
            {users.map((user) => (
              <Grid.Col key={user.id} span={{ base: 12, xs: 6, sm: 4 }}>
                <UserCard user={user} />
              </Grid.Col>
            ))}
          </Grid>
        )}

        {users.length === 0 && (
          <Box ta="center" py="xl">
            <L2KText variant="tertiary">No users found</L2KText>
          </Box>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <Group justify="center">
            <Pagination
              total={pagination.total_pages}
              value={pagination.page}
              onChange={handlePageChange}
              color="orange"
              styles={{
                control: {
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  borderColor: "rgba(255, 255, 255, 0.1)",
                  color: "#D0D0D0",
                  "&[data-active]": {
                    backgroundColor: "#FF9800",
                    borderColor: "#FF9800",
                    color: "#171717",
                  },
                },
              }}
            />
          </Group>
        )}

        {/* Delete Confirmation Modal */}
        <Modal
          opened={deleteModal}
          onClose={closeDelete}
          title="Delete User"
          styles={{
            header: { backgroundColor: "#171717" },
            body: { backgroundColor: "#171717" },
            title: { color: "#F0F0F0", fontFamily: "'Public Sans', sans-serif" },
          }}
        >
          <Stack gap="md">
            <L2KText>
              Are you sure you want to delete <strong>{userToDelete?.name || userToDelete?.email}</strong>?
            </L2KText>
            <L2KText variant="secondary" size="sm">
              This action cannot be undone. All user data will be permanently deleted.
            </L2KText>
            <Group justify="flex-end" mt="md">
              <L2KButton variant="ghost" onClick={closeDelete}>
                Cancel
              </L2KButton>
              <L2KButton
                variant="outline"
                onClick={handleDelete}
                style={{ borderColor: "#ef4444", color: "#ef4444" }}
              >
                Delete
              </L2KButton>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </>
  );
}

UsersIndex.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>;
